import { useState, useEffect } from 'react';
import { Plus, X, Swords, Users, MapPin, Shield, GraduationCap, UsersRound } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useCoachRole } from '@/hooks/useCoachRole';

const createOptions = [
  { icon: Swords, label: 'Tạo trận đấu', path: '/matches/create' },
  { icon: UsersRound, label: 'Tạo đội', path: '/profile/teams', action: 'team' },
  { icon: Shield, label: 'Thách đấu CLB', path: '/clubs', action: 'club-challenge' },
  { icon: Users, label: 'Tạo câu lạc bộ', path: '/clubs', action: 'club' },
  { icon: MapPin, label: 'Đăng sân trống', path: '/courts/create-slot' },
  { icon: GraduationCap, label: 'Tạo lớp học', path: '/classes', action: 'class', coachOnly: true },
];

const FAB_SEEN_KEY = 'fab_seen';

export function FloatingCreateButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenFab, setHasSeenFab] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: isCoach } = useCoachRole();

  useEffect(() => {
    const seen = localStorage.getItem(FAB_SEEN_KEY);
    setHasSeenFab(seen === 'true');
  }, []);

  const handleFabClick = () => {
    if (!hasSeenFab) {
      localStorage.setItem(FAB_SEEN_KEY, 'true');
      setHasSeenFab(true);
    }
    setIsOpen(!isOpen);
  };

  // Hide FAB on auth and admin pages
  const hiddenPaths = ['/auth', '/admin'];
  const shouldHide = hiddenPaths.some(path => location.pathname === path || location.pathname.startsWith('/admin/'));
  
  if (shouldHide) {
    return null;
  }

  // Filter options based on user role
  const visibleOptions = createOptions.filter(option => {
    if (option.coachOnly) {
      return isCoach;
    }
    return true;
  });

  const handleOptionClick = (option: typeof createOptions[0]) => {
    setIsOpen(false);
    if (option.action === 'club-challenge') {
      navigate('/clubs?action=challenge');
    } else if (option.action === 'club') {
      navigate('/clubs?action=create');
    } else if (option.action === 'class') {
      navigate('/classes?action=create');
    } else if (option.action === 'team') {
      navigate('/profile/teams?action=create');
    } else {
      navigate(option.path);
    }
  };

  return (
    <>
      {/* Backdrop Blur Overlay - only render when open */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm animate-in fade-in duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div className="fixed bottom-24 right-4 z-[70] flex flex-col items-end gap-2 pointer-events-none">
        {/* Options Menu */}
        <div className={cn(
          "flex flex-col gap-2 transition-all duration-300 origin-bottom pointer-events-auto",
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        )}>
          {visibleOptions.map((option, index) => (
            <button
              key={option.label}
              onClick={() => handleOptionClick(option)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-full bg-card border border-border shadow-lg",
                "hover:bg-accent transition-all duration-200",
                "animate-in slide-in-from-bottom-2 fade-in"
              )}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <option.icon className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-foreground whitespace-nowrap">{option.label}</span>
            </button>
          ))}
        </div>

        {/* Main FAB Button */}
        <button
          onClick={handleFabClick}
          aria-label={isOpen ? "Đóng menu tạo mới" : "Mở menu tạo mới"}
          className={cn(
            "w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg pointer-events-auto",
            "flex items-center justify-center transition-all duration-300",
            "hover:bg-primary/90 active:scale-95",
            isOpen ? "rotate-45" : (!hasSeenFab && "animate-pulse")
          )}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
        </button>
      </div>
    </>
  );
}
